import type { AssetType } from '~/types';
import type { AdvancedCreateFromFileSettings } from '../types';
import { useDialog } from '@workramp/ui';
import { useGo1Modal } from '~/elc/features/go1';
import AssetPickerTypes from '~/components/common/asset-picker/asset-picker-types';
import { MixPanel } from '~/common';
import { CreateContentModal, CreateContentProviderContentModal, CreateFromFileModal } from '../components';
import { MIXPANEL_CREATE_FROM_FILE_START } from '../constants';
import { CreateGuideFromDeepSearchModal } from '../components/CreateGuideFromDeepSearchModal';
import { useContentProviderContentCreatedSubscription } from './useContentProviderContentCreatedSubscription';

interface CreateContentArgs {
  type: AssetType;
  initialName?: string;
  itemFolderId?: string;
  advancedSettings?: AdvancedCreateFromFileSettings;
  onSuccess?: () => void;
}

export function useCreateContent(): (args: CreateContentArgs) => void {
  const createContentDialog = useDialog(CreateContentModal);
  const createContentProviderContentDialog = useDialog(CreateContentProviderContentModal);
  const { createSubscription } = useContentProviderContentCreatedSubscription();
  const createFromFileDialog = useDialog(CreateFromFileModal);
  const { showGo1Modal } = useGo1Modal();
  const createGuideFromDeepSearchModal = useDialog(CreateGuideFromDeepSearchModal);

  const openCreateContentModal = (args: CreateContentArgs) => {
    createContentDialog.open({
      ...args,
      onCreateFromFile: (name) => {
        MixPanel.track(MIXPANEL_CREATE_FROM_FILE_START);
        createContentDialog.close();
        openCreateFromFileModal({ ...args, initialName: name });
      },
      onCreateFromDeepSearch: (name) => {
        createContentDialog.close();
        openCreateGuideFromDeepSearchModal({ ...args, initialName: name });
      },
      onClose: createContentDialog.close,
      onExited: createContentDialog.remove,
    });
  };

  const openCreateGuideFromDeepSearchModal = (args: CreateContentArgs) => {
    createGuideFromDeepSearchModal.open({
      ...args,
      onClose: createGuideFromDeepSearchModal.close,
    });
  };

  const openCreateFromFileModal = (args: CreateContentArgs) => {
    createFromFileDialog.open({
      ...args,
      onStartBlank: (name) => {
        createFromFileDialog.close();
        openCreateContentModal({ ...args, initialName: name });
      },
      onClose: createFromFileDialog.close,
      onExited: createFromFileDialog.remove,
    });
  };

  const openCreateContentProviderContentModal = (args: CreateContentArgs) => {
    if (args.type === AssetPickerTypes.LINKEDIN_LEARNING || args.type === AssetPickerTypes.UDEMY) {
      createContentProviderContentDialog.open({
        ...args,
        provider: args.type,
        onCreate: createSubscription,
        onClose: createContentProviderContentDialog.close,
        onExited: createContentProviderContentDialog.remove,
        itemFolderId: args.itemFolderId,
      });
    } else {
      throw new Error(`Invalid content provider slug: ${args.type}`);
    }
  };

  const create = (args: CreateContentArgs) => {
    if (args.type === AssetPickerTypes.GO1_CONTENT) {
      showGo1Modal();
      return;
    }

    if (args.type === AssetPickerTypes.LINKEDIN_LEARNING || args.type === AssetPickerTypes.UDEMY) {
      openCreateContentProviderContentModal(args);
      return;
    }

    openCreateContentModal(args);
  };

  return create;
}
